/**
 * Activities tab — admins create/manage data-for-points activities (plan 1904).
 * Clones the Earn Rules tab layout. Switch state is DERIVED from query data
 * (no optimistic local state) so a backend reject (e.g. re-enable hits the
 * unique-active validate) snaps the switch back to server truth.
 */

import { useState } from "react";
import { Loader2, Cake, User, Zap, Plus, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { AddActivityDialog } from "./AddActivityDialog";
import { EditActivityDialog } from "./EditActivityDialog";
import {
  useActivityRules,
  useToggleActivity,
  type ActivityRule,
} from "../api/activitiesAdminHooks";

const SECTION_ICON: Record<string, LucideIcon> = {
  birthday: Cake,
  profile: User,
};

function validityLabel(rule: ActivityRule): { text: string; expired: boolean } {
  const today = new Date().toISOString().split("T")[0];
  if (rule.valid_to && rule.valid_to < today)
    return { text: "สิ้นสุดแล้ว", expired: true };
  if (rule.valid_to) return { text: `หมดอายุ ${rule.valid_to}`, expired: false };
  return { text: "ไม่มีกำหนด", expired: false };
}

export function ActivitiesTab() {
  const rulesQuery = useActivityRules();
  const toggleMutation = useToggleActivity();
  const [addOpen, setAddOpen] = useState(false);
  const [editRule, setEditRule] = useState<ActivityRule | null>(null);

  const handleToggle = (rule: ActivityRule) => {
    toggleMutation.mutate(
      { name: rule.name, enabled: rule.enabled ? 0 : 1 },
      {
        onSuccess: () => toast.success("บันทึกสำเร็จ"),
        onError: (err: Error) => toast.error(err.message || "เกิดข้อผิดพลาด"),
      },
    );
  };

  if (rulesQuery.isLoading) {
    return (
      <div className="space-y-3 pt-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-lg" />
        ))}
      </div>
    );
  }

  const rules = rulesQuery.data?.rules ?? [];

  return (
    <div className="space-y-3 pt-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> สร้างกิจกรรม
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Zap className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">ยังไม่มีกิจกรรม</p>
          <p className="text-sm text-muted-foreground">
            สร้างกิจกรรมเพื่อให้ลูกค้าสะสมแต้มจากการทำสิ่งต่างๆ เช่น
            &quot;กรอกวันเกิด รับ 3 แต้ม&quot;
          </p>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            สร้างกิจกรรม
          </Button>
        </div>
      ) : (
        rules.map((rule) => {
          const Icon = SECTION_ICON[rule.section] ?? Zap;
          const label = rule.labels?.section_label || rule.section;
          const path = [
            rule.labels?.page_label ?? rule.page,
            rule.labels?.section_label ?? rule.section,
            rule.labels?.action_label ?? rule.action,
          ].join(" › ");
          const validity = validityLabel(rule);
          const togglingThis =
            toggleMutation.isPending &&
            toggleMutation.variables?.name === rule.name;
          return (
            <div
              key={rule.name}
              className={`flex items-center gap-3 rounded-lg border p-3 ${
                validity.expired ? "opacity-50" : ""
              }`}
            >
              <Icon className="h-5 w-5 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{path}</p>
                {rule.one_time_use === 0 && (
                  <p className="text-xs text-sky-600">
                    ทำซ้ำได้
                    {rule.max_points_per_customer > 0
                      ? ` · เพดาน ${rule.max_points_per_customer} แต้ม`
                      : ""}
                  </p>
                )}
              </div>
              <span
                className={`text-xs ${
                  validity.expired ? "text-red-400" : "text-muted-foreground"
                }`}
              >
                {rule.points} แต้ม · {validity.text}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditRule(rule)}
                disabled={validity.expired}
              >
                แก้ไข
              </Button>
              <Switch
                checked={!!rule.enabled}
                onCheckedChange={() => handleToggle(rule)}
                disabled={togglingThis || validity.expired}
              />
              {togglingThis && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </div>
          );
        })
      )}

      <AddActivityDialog open={addOpen} onOpenChange={setAddOpen} />
      <EditActivityDialog
        rule={editRule}
        open={!!editRule}
        onOpenChange={(o) => {
          if (!o) setEditRule(null);
        }}
      />
    </div>
  );
}
